import { useMutation } from "@vue/apollo-composable";
import {
  CreateScheduledPaymentDocument,
  PaymentEndCondition,
  PaymentFrequency,
  RecipientType,
  ScheduledPaymentsDocument,
  StopScheduledPaymentDocument,
} from "@/api/generated/graphql";
import type { StopScheduledPaymentMutationVariables } from "@/api/generated/graphql";
import { toIsoDateString } from "@moroku/money-webapp-components";

export const scheduledPaymentsQuery = ScheduledPaymentsDocument;

export type Recipient =
  | { type: RecipientType.AccountRecipient; accountNumber: string; bsb: string }
  | { type: RecipientType.BpayRecipient; billerCode: string; bpayCrn: string }
  | { type: RecipientType.PayIdRecipient; payId: string };

export type ScheduleOptions = {
  startDate: Date;
  frequency: PaymentFrequency;
} & (
  | {
      endCondition: PaymentEndCondition.AfterCount;
      maxCount: number;
    }
  | {
      endCondition: PaymentEndCondition.OnDate;
      endDate: Date;
    }
  | {
      endCondition: PaymentEndCondition.Never;
    }
);

export async function createScheduledPayment(options: {
  fromAccountId: string;
  name: string;
  amount: number;
  description: string;
  recipient: Recipient;
  scheduleOptions: ScheduleOptions;
}) {
  const { onDone, onError, mutate } = useMutation(CreateScheduledPaymentDocument);
  const resFuture = new Promise((resolve, reject) => {
    onDone(result => {
      resolve(result);
    });
    onError(error => {
      reject(error);
    });
  });

  const input = {
    amount: options.amount,
    description: options.description,
    fromAccountId: options.fromAccountId,

    startDate: toIsoDateString(options.scheduleOptions.startDate),
    endCondition: options.scheduleOptions.endCondition,
    frequency: options.scheduleOptions.frequency,

    ...(options.scheduleOptions.endCondition === PaymentEndCondition.OnDate && {
      endDate: toIsoDateString(options.scheduleOptions.endDate),
    }),
    ...(options.scheduleOptions.endCondition === PaymentEndCondition.AfterCount && {
      maxCount: options.scheduleOptions.maxCount,
    }),

    recipient: {
      name: options.name,
      type: options.recipient.type,
      ...(options.recipient.type === RecipientType.AccountRecipient && {
        account: {
          accountNumber: options.recipient.accountNumber,
          bsbNumber: options.recipient.bsb,
        },
      }),
      ...(options.recipient.type === RecipientType.BpayRecipient && {
        bpay: {
          crn: options.recipient.bpayCrn,
          billerCode: options.recipient.billerCode,
        },
      }),
      ...(options.recipient.type === RecipientType.PayIdRecipient && {
        payId: {
          payId: options.recipient.payId,
        },
      }),
    },
  };

  await mutate({ input });

  return resFuture;
}

export async function stopScheduledPayment({ accountId, scheduledPaymentId }: StopScheduledPaymentMutationVariables) {
  const { onDone, onError, mutate } = useMutation(StopScheduledPaymentDocument);
  const resFuture = new Promise((resolve, reject) => {
    onDone(result => {
      resolve(result);
    });
    onError(error => {
      reject(error);
    });
  });

  await mutate({
    accountId,
    scheduledPaymentId,
  });

  return resFuture;
}
